import { endOfDay, startOfDay } from 'date-fns';
import { z } from 'zod';
import {
  PAGINATION_PAGE_SIZE_DEFAULT,
  PAGINATION_SORT_DIRECTION,
} from '~/constants';

import { withoutObjectKeysWhereValueIs } from '@vestigas/utils';
import { parseDate } from '~/utils';

import {
  REPROCESSING_INVOICE_CHANNEL,
  REPROCESSING_INVOICE_ORDER_BY,
  REPROCESSING_INVOICE_STATUS,
} from './constants';

export const zodReprocessingInvoicesFilterModelSchema = z.object({
  channel: z
    .union([
      z.enum(REPROCESSING_INVOICE_CHANNEL).transform((value) => [value]),
      z.array(z.enum(REPROCESSING_INVOICE_CHANNEL)),
    ])
    .refine((channels) => channels.length === new Set(channels).size, {
      error: 'Channel values must be unique',
    })
    .prefault([]),
  createdAfter: z
    .union([z.iso.datetime(), z.string()])
    .nullish()
    .transform((value) =>
      value ? (startOfDay(parseDate(value))?.toISOString() ?? null) : null,
    ),
  createdBefore: z
    .union([z.iso.datetime(), z.string()])
    .nullish()
    .transform((value) =>
      value ? (endOfDay(parseDate(value))?.toISOString() ?? null) : null,
    ),
  dryRun: z
    .union([
      z.boolean(),
      z
        .string()
        .refine((value) => value === 'true' || value === 'false', {
          error: "String must be 'true' or 'false'",
        })
        .transform((value) => value === 'true'),
    ])
    .optional(),
  needsAiParsing: z
    .union([
      z.boolean(),
      z
        .string()
        .refine((value) => value === 'true' || value === 'false', {
          error: "String must be 'true' or 'false'",
        })
        .transform((value) => value === 'true'),
    ])
    .optional(),
  searchString: z.string().prefault(''),
  status: z
    .union([
      z.enum(REPROCESSING_INVOICE_STATUS).transform((value) => [value]),
      z.array(z.enum(REPROCESSING_INVOICE_STATUS)),
    ])
    .refine((statuses) => statuses.length === new Set(statuses).size, {
      error: 'Status values must be unique',
    })
    .prefault([]),
});

export const zodReprocessingInvoicesQueryParamsSchema =
  zodReprocessingInvoicesFilterModelSchema.extend({
    limit: z.number().gte(1).optional().prefault(PAGINATION_PAGE_SIZE_DEFAULT),
    offset: z.number().nonnegative().optional().prefault(0),
    orderBy: z
      .enum(REPROCESSING_INVOICE_ORDER_BY)
      .optional()
      .prefault('created_on'),
    sortOrder: z
      .preprocess(
        (value) => String(value).toUpperCase(),
        z.enum(PAGINATION_SORT_DIRECTION),
      )
      .optional()
      .prefault('DESC'),
  });

export const zodReprocessingInvoiceSchema = z
  .object({
    buyerName: z.string().nullish(),
    channel: z.enum(REPROCESSING_INVOICE_CHANNEL),
    comment: z.string().optional(),
    createdOn: z.iso.datetime(),
    currency: z.enum(['EUR']).nullish(),
    dryRun: z.boolean(),
    emailReceivedDatetime: z.iso.datetime().nullish(),
    emailSender: z.string().nullish(),
    emailSubject: z.string().nullish(),
    id: z.uuid(),
    invoiceDate: z.iso.datetime().nullish(),
    invoiceNumber: z.string().nullish(),
    needsAiParsing: z.boolean(),
    processFinishedOn: z.iso.datetime().optional(),
    recipientMatchedCompanyId: z.uuid().nullish(),
    recipientMatchedCompanyName: z.string().nullish(),
    sellerName: z.string().nullish(),
    status: z.enum(REPROCESSING_INVOICE_STATUS),
    statusDetails: z.record(z.string(), z.any()).optional(),
    totalAmount: z.string().nullish(),
    watermarkType: z.string().nullish(),
  })
  .transform((data) =>
    withoutObjectKeysWhereValueIs(data, [null, undefined, '']),
  );

export const zodReprocessingInvoicesResponseSchema = z.object({
  data: z.array(zodReprocessingInvoiceSchema),
  hasNextPage: z.boolean(),
  paginatedCount: z.number(),
  totalCount: z.number(),
});
